import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";

// Types
export interface CellLineItem {
  model_id: string;
  model_name?: string | null;
  sample_id?: string | null;
  patient_id?: string | null;
  parent_id?: string | null;
  synonyms?: string | null;
  tissue?: string | null;
  cancer_type?: string | null;
  cancer_type_ncit_id?: string | null;
  tissue_status?: string | null;
  sample_site?: string | null;
  cancer_type_detail?: string | null;
  model_type?: string | null;
  msi_status?: string | null;
  rowData?: any;
  [key: string]: unknown;
}

interface BaseParams {
  library_name: string;
  group_name: string;
  round: number;
}

export interface iCellLineListState {
  isLoading: boolean;
  isSaving: boolean;
  message: string | null;
  response: CellLineItem[];
  errorStatus: number | null;
  saveError: string | null;
}

const initialState: iCellLineListState = {
  isLoading: false,
  isSaving: false,
  message: "",
  response: [],
  errorStatus: null,
  saveError: null,
};

//  { rowData: [...] } 또는 배열을 반환하는 경우 모두 처리
function extractCellLineList(data: unknown): CellLineItem[] {
  let list: any[] = [];
  if (Array.isArray(data)) {
    list = data;
  } else if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.rowData)) list = obj.rowData;
    else {
      const firstArrayVal = Object.values(obj).find((v) => Array.isArray(v));
      if (firstArrayVal) list = firstArrayVal as any[];
    }
  }
  // { cell_line: "1181N1" } 형태인 경우 model_id 로 정규화
  return list.map((item: any) => {
    if (typeof item.cell_line === "string" && !item.model_id) {
      return { ...item, model_id: item.cell_line, model_name: item.cell_line };
    }
    return item as CellLineItem;
  });
}

// GET
export const cellLineListAPI = createAsyncThunk(
  "CELL_LINE_LIST",
  async (params: BaseParams, { rejectWithValue }) => {
    try {
      const pathParam = "/workflow/drug-response/cell-line-list/";
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        params,
        { withCredentials: true }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue({
        message: apiError.response?.data?.error || apiError.message || "An error occurred",
        status: apiError.response?.status || null,
      });
    }
  }
);

// UPDATE
export const updateCellLineAPI = createAsyncThunk(
  "CELL_LINE_UPDATE",
  async (
    params: BaseParams & { model_id: string; updates: Partial<CellLineItem> },
    { rejectWithValue }
  ) => {
    try {
      const pathParam = "/workflow/drug-response/cell-line-update/";
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.patch(
        `${djangoEndpoint}${pathParam}`,
        params,
        { withCredentials: true }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue({
        message: apiError.response?.data?.error || apiError.message || "Update failed",
        status: apiError.response?.status || null,
      });
    }
  }
);

// ADD 
export const addCellLineAPI = createAsyncThunk(
  "CELL_LINE_ADD",
  async (
    params: BaseParams & { cell_line: CellLineItem },
    { rejectWithValue }
  ) => {
    try {
      const pathParam = "/workflow/drug-response/cell-line-add/";
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        params,
        { withCredentials: true }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue({
        message: apiError.response?.data?.error || apiError.message || "Add failed",
        status: apiError.response?.status || null,
      });
    }
  }
);

// DELETE
export const deleteCellLineAPI = createAsyncThunk(
  "CELL_LINE_DELETE",
  async (
    params: BaseParams & { model_id: string },
    { rejectWithValue }
  ) => {
    try {
      const pathParam = "/workflow/drug-response/cell-line-delete/";
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.delete(
        `${djangoEndpoint}${pathParam}`,
        { data: params, withCredentials: true }
      );
      return { data: response.data, status: response.status, model_id: params.model_id };
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue({
        message: apiError.response?.data?.error || apiError.message || "Delete failed",
        status: apiError.response?.status || null,
      });
    }
  }
);

// Slice
export const cellLineSlice = createSlice({
  name: "cellLineList",
  initialState,
  reducers: {
    resetCellLine: (state) => {
      state.response = [];
      state.message = "";
      state.isLoading = false;
      state.isSaving = false;
      state.errorStatus = null;
      state.saveError = null;
    },
    // Optimistic: 서버 응답 전 즉시 로컬 반영
    optimisticUpdateCellLine: (
      state,
      action: PayloadAction<{ model_id: string; updates: Partial<CellLineItem> }>
    ) => {
      const list = Array.isArray(state.response) ? state.response : [];
      const idx = list.findIndex((c) => c.model_id === action.payload.model_id);
      if (idx !== -1) {
        state.response[idx] = { ...state.response[idx], ...action.payload.updates };
      }
    },
    optimisticDeleteCellLine: (state, action: PayloadAction<string>) => {
      const list = Array.isArray(state.response) ? state.response : [];
      state.response = list.filter((c) => c.model_id !== action.payload);
    },
    optimisticAddCellLine: (state, action: PayloadAction<CellLineItem>) => {
      if (!Array.isArray(state.response)) state.response = [];
      state.response.push(action.payload);
    },
    clearSaveError: (state) => {
      state.saveError = null;
    },
    // 서버 실패 시 롤백용: 전체 목록 교체
    rollbackCellLineList: (state, action: PayloadAction<CellLineItem[]>) => {
      state.response = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── GET ──
      .addCase(cellLineListAPI.pending, (state) => {
        state.isLoading = true;
        state.message = "";
        state.errorStatus = null;
      })
      .addCase(cellLineListAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        // 백엔드가 { rowData: [...] } 형태로 내려오므로 배열만 추출
        state.response = extractCellLineList(action.payload.data);
        state.errorStatus = action.payload.status;
        state.message = "";
      })
      .addCase(cellLineListAPI.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { message: string; status: number | null };
        state.message = payload.message;
        state.errorStatus = payload.status;
      })
      // ── UPDATE ──
      .addCase(updateCellLineAPI.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(updateCellLineAPI.fulfilled, (state, action) => {
        state.isSaving = false;
        const list = extractCellLineList(action.payload.data);
        if (list.length > 0) state.response = list;
      })
      .addCase(updateCellLineAPI.rejected, (state, action) => {
        state.isSaving = false;
        const payload = action.payload as { message: string };
        state.saveError = payload.message;
      })
      // ── ADD ──
      .addCase(addCellLineAPI.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(addCellLineAPI.fulfilled, (state, action) => {
        state.isSaving = false;
        const list = extractCellLineList(action.payload.data);
        if (list.length > 0) state.response = list;
      })
      .addCase(addCellLineAPI.rejected, (state, action) => {
        state.isSaving = false;
        const payload = action.payload as { message: string };
        state.saveError = payload.message;
      })
      // ── DELETE ──
      .addCase(deleteCellLineAPI.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(deleteCellLineAPI.fulfilled, (state, action) => {
        state.isSaving = false;
        const list = extractCellLineList(action.payload.data);
        if (list.length > 0) state.response = list;
      })
      .addCase(deleteCellLineAPI.rejected, (state, action) => {
        state.isSaving = false;
        const payload = action.payload as { message: string };
        state.saveError = payload.message;
      });
  },
});

export const {
  resetCellLine,
  optimisticUpdateCellLine,
  optimisticDeleteCellLine,
  optimisticAddCellLine,
  clearSaveError,
  rollbackCellLineList,
} = cellLineSlice.actions;

export default cellLineSlice.reducer;